import React, { FormEventHandler, useCallback, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import requestGetHeroPlannerOfferDetails from '~/queries/customerCommunication/requestGetHeroPlannerOfferDetails';

import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { Alert, AlertTitle, Stack, TextField } from '@mui/material';
import Button from '@mui/material/Button';

import { RequestStatus } from '~/consts/requestConstants';

import { OfferValidationResult, findDuplicates, parseOfferIds, validateOfferId } from '~/utils/offerProcessingUtils';

const OFFER_ID_INPUT_NAME = 'schedule-new-offer-id';

interface Props {
  countryGroupId: string;
  countryId: string;
  currentOfferIds: Array<string>;
  onOfferDetailsReady: (offerDetails: Array<CustomerCommunication.HeroPlannerOfferDetails>) => void;
  maxOffersCap: number;
  remainingSlots: number;
}

interface OfferRequestState {
  offerId: string;
  status: RequestStatus;
  error?: string;
  result?: CustomerCommunication.HeroPlannerOfferDetails;
}

function ScheduleNewHeroOfferForm({
  countryGroupId,
  countryId,
  currentOfferIds,
  onOfferDetailsReady,
  maxOffersCap,
  remainingSlots,
}: Props) {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const formRef = useRef<HTMLFormElement>(null);
  const offerIdInputRef = useRef<HTMLInputElement>(null);

  // Track state for each offer request
  const [offerRequests, setOfferRequests] = useState<Array<OfferRequestState>>([]);
  const [duplicateOffers, setDuplicateOffers] = useState<Array<string>>([]);
  const [exceedingOffers, setExceedingOffers] = useState<Array<string>>([]);
  const [validationErrors, setValidationErrors] = useState<Array<OfferValidationResult>>([]);

  const requestOfferDetails = useCallback(
    async (offerId: string): Promise<OfferRequestState> => {
      const params = [brand, countryGroupId, countryId, offerId] as const;

      try {
        const result = await requestGetHeroPlannerOfferDetails(...params);
        return {
          offerId,
          status: RequestStatus.FULFILLED,
          result,
        };
      } catch (error) {
        return {
          offerId,
          status: RequestStatus.REJECTED,
          error: String(error),
        };
      }
    },
    [brand, countryId, countryGroupId],
  );

  const handleInputChange = useCallback(() => {
    setDuplicateOffers([]);
    setExceedingOffers([]);
    setValidationErrors([]);
  }, []);

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      event.preventDefault();
      try {
        const formData = new FormData(event.currentTarget);
        const inputValue = String(formData.get(OFFER_ID_INPUT_NAME));
        const offerIds = parseOfferIds(inputValue);

        if (offerIds.length === 0) {
          return;
        }

        // Check for duplicates before deduplication (for alert)
        const duplicates = findDuplicates(offerIds);
        setDuplicateOffers(duplicates);
        const uniqueOfferIds = [...new Set(offerIds)];

        // Validate all offer IDs (for alert)
        const invalidOffers = uniqueOfferIds
          .map((id) => ({
            id,
            validation: validateOfferId(id, currentOfferIds),
          }))
          .filter(({ validation }) => !validation.isValid);

        if (invalidOffers.length > 0) {
          setValidationErrors(invalidOffers.map(({ validation }) => validation));
        }

        // remove invalid offers from uniqueOfferIds
        const validOfferIds = uniqueOfferIds.filter((id) => !invalidOffers.some((invalid) => invalid.id === id));

        // Check if adding these offers would exceed the limit (for alert)
        if (validOfferIds.length > remainingSlots) {
          const exceeding = validOfferIds.slice(remainingSlots);
          setExceedingOffers(exceeding);
          validOfferIds.splice(remainingSlots);
        }

        if (validOfferIds.length === 0) {
          return;
        }

        setOfferRequests(
          validOfferIds.map((offerId) => ({
            offerId,
            status: RequestStatus.PENDING,
          })),
        );
        const results = await Promise.all(validOfferIds.map((offerId) => requestOfferDetails(offerId)));
        setOfferRequests(results);

        const successfulOffers = results
          .filter((req) => req.status === RequestStatus.FULFILLED && req.result)
          .map((req) => req.result);

        if (successfulOffers.length > 0) {
          onOfferDetailsReady(successfulOffers);
          formRef.current?.reset();
        }
      } catch (error) {
        console.error('Error processing offers:', error);
      }
    },
    [currentOfferIds, requestOfferDetails, onOfferDetailsReady, remainingSlots],
  );

  const isProcessing = offerRequests.some((req) => req.status === RequestStatus.PENDING);

  const failedOffers = offerRequests
    .filter((req) => req.status === RequestStatus.REJECTED)
    .map((req) => ({
      offerId: req.offerId,
      error: req.error || 'Unknown error',
    }));

  const hasWarnings = failedOffers.length > 0;
  const hasDuplicates = duplicateOffers.length > 0;
  const hasExceeding = exceedingOffers.length > 0;
  const hasValidationErrors = validationErrors.length > 0;
  const isDisabled = remainingSlots <= 0 || isProcessing;

  // Minimum offers logic
  const minOffersCap = 2;
  const currentOfferCount = currentOfferIds.length;
  const offersNeededForMinimum = Math.max(0, minOffersCap - currentOfferCount);

  const getMinimumOffersText = () => {
    if (offersNeededForMinimum === 0) {
      return '';
    }
    if (currentOfferCount === 0) {
      return ` (minimum ${minOffersCap} offers required)`;
    }
    return ` (add ${offersNeededForMinimum} more for minimum)`;
  };

  const placeholderText =
    remainingSlots <= 0
      ? `Max offer cap ${maxOffersCap} reached`
      : `Enter offer ID(s) - up to ${remainingSlots} ${
          remainingSlots === 1 ? 'offer' : 'offers'
        }${getMinimumOffersText()}`;

  return (
    <Stack direction="column" gap={1}>
      <form ref={formRef} onSubmit={handleSubmission}>
        <Stack direction="row" gap={1} alignItems="start">
          <TextField
            inputRef={offerIdInputRef}
            name={OFFER_ID_INPUT_NAME}
            required
            placeholder={placeholderText}
            disabled={isProcessing}
            onChange={handleInputChange}
            sx={{ width: '90%' }}
            multiline
          />
          <Button type="submit" loading={isProcessing} disabled={isDisabled}>
            Add Offer
          </Button>
        </Stack>
      </form>

      {hasValidationErrors && (
        <Alert severity="error" icon={<ErrorIcon />}>
          <AlertTitle>Validation errors</AlertTitle>
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            {validationErrors.map(({ offerId, error }) => `Offer ${offerId}: ${error}`).join('\n')}
          </pre>
        </Alert>
      )}

      {hasDuplicates && (
        <Alert severity="info" icon={<WarningIcon />}>
          <AlertTitle>Duplicate offers detected</AlertTitle>
          <pre>The following offers appear multiple times. Only the first occurrence is considered:</pre>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{duplicateOffers.join(', ')}</pre>
        </Alert>
      )}

      {hasExceeding && (
        <Alert severity="info" icon={<WarningIcon />}>
          <AlertTitle>Maximum offers limit reached</AlertTitle>
          <pre>The following offers were not added as they would exceed the limit of {maxOffersCap}:</pre>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{exceedingOffers.join(', ')}</pre>
        </Alert>
      )}

      {hasWarnings && (
        <Alert severity="warning" icon={<WarningIcon />}>
          <AlertTitle>Some offers could not be added</AlertTitle>
          <pre>Only successful offers were added. The order of offers may differ. Please verify.</pre>
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            {failedOffers.map(({ offerId, error }) => `Offer ${offerId}: ${error}`).join('\n')}
          </pre>
        </Alert>
      )}
    </Stack>
  );
}

export default ScheduleNewHeroOfferForm;
